<script setup lang="ts">
  defineOptions({ name: 'CPTabs', inheritAttrs: false })

  // External library imports
  import type { Directive, PropType } from 'vue'

  import { HLTabs } from '@platform-ui/highrise'

  // Type imports

  // Props definitions
  const props = defineProps({
    id: {
      type: String,
      required: false,
      default: undefined,
    },
    type: {
      type: String as PropType<'line' | 'segment'>,
      required: false,
      default: 'line',
    },
    placement: {
      type: String as PropType<'top' | 'bottom' | 'left' | 'right'>,
      required: false,
      default: 'top',
    },
    value: {
      type: [String, Number],
      required: false,
      default: undefined,
    },
    defaultValue: {
      type: [String, Number] as PropType<string | number>,
      required: false,
      default: undefined,
    },
    justifyContent: {
      type: String as PropType<'start' | 'end' | 'space-around' | 'space-between' | 'space-evenly' | 'center'>,
      required: false,
      default: 'start',
    },
    size: {
      type: String as PropType<'sm' | 'md' | 'lg'>,
      required: false,
      default: 'md',
    },
    maxVisibleTabs: {
      type: Number,
      required: false,
      default: 5,
    },
    ariaLabel: {
      type: String,
      required: false,
      default: undefined,
    },
    /**
     * Let the parent surface show through instead of painting --cp-background-500 behind the tab strip.
     * Only the strip is cleared: in `type="segment"` the selected tab keeps its fill, since that fill is the selection indicator.
     */
    noBackground: {
      type: Boolean,
      required: false,
      default: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    'update:value': [value: string | number]
  }>()

  // Component-owned token bridge intentionally takes precedence over consumer --n-* styles.
  const tabsStyles = {
    '--n-color': 'var(--cp-background-500)',
    '--n-color-segment': 'var(--cp-surface-500)',
    '--n-tab-color': 'var(--cp-background-500)',
    '--n-tab-color-hover': 'var(--cp-hover)',
    '--n-tab-color-segment': 'color-mix(in oklab, var(--cp-primary-500) 10%, var(--cp-background-500))',
    '--n-tab-border-color': 'var(--cp-border-500)',
    '--n-tab-text-color': 'var(--cp-font-600)',
    '--n-tab-text-color-active': 'var(--cp-primary-500)',
    '--n-tab-text-color-disabled': 'var(--cp-font-400)',
    '--n-tab-text-color-hover': 'var(--cp-primary-500)',
    '--n-bar-color': 'var(--cp-primary-500)',
    '--n-pane-text-color': 'var(--cp-font-700)',
  }

  // User-defined functions
  const handleUpdateValue = (value: string | number) => {
    emit('update:value', value)
  }

  const setTablistAccessibleLabel = (element: HTMLElement, ariaLabel?: string) => {
    const tablist = element.querySelector<HTMLElement>('[role="tablist"]')
    if (!tablist) return

    if (ariaLabel) {
      tablist.setAttribute('aria-label', ariaLabel)
    } else {
      tablist.removeAttribute('aria-label')
    }
  }

  /**
   * Highrise renders `HLTabs` as a generic root div containing the actual
   * `[role="tablist"]`. Allowing `aria-label` to fall through places it on the
   * generic div and triggers `aria-prohibited-attr`, so keep the attribute in
   * CPTabs and apply it to the semantic tablist until Highrise exposes a native
   * tablist labelling prop.
   */
  const vTablistAccessibleLabel: Directive<HTMLElement, string | undefined> = {
    mounted: (element, binding) => setTablistAccessibleLabel(element, binding.value),
    updated: (element, binding) => setTablistAccessibleLabel(element, binding.value),
  }
</script>

<template>
  <HLTabs
    v-bind="$attrs"
    :id="props.id"
    v-tablist-accessible-label="props.ariaLabel"
    :style="tabsStyles"
    :class="['cp-tabs', { 'cp-tabs--no-background': props.noBackground }]"
    :type="props.type"
    :placement="props.placement"
    :value="props.value"
    :default-value="props.defaultValue"
    :justify-content="props.justifyContent"
    :size="props.size"
    :max-visible-tabs="props.maxVisibleTabs"
    @update:value="handleUpdateValue"
  >
    <slot />
  </HLTabs>
</template>

<style>
  .cp-tabs {
    --n-color: var(--cp-background-500) !important;
    --n-tab-color: var(--cp-background-500) !important;
    --n-tab-color-hover: var(--cp-hover) !important;
    --n-color-segment: var(--cp-surface-500) !important;
    background-color: var(--cp-background-500) !important;
  }

  .cp-tabs .hr-tabs-nav,
  .cp-tabs .n-tabs-nav,
  .cp-tabs .hr-tabs-nav-scroll-wrapper,
  .cp-tabs .n-tabs-nav-scroll-wrapper,
  .cp-tabs .hr-tabs-pane-wrapper,
  .cp-tabs .n-tabs-pane-wrapper,
  .cp-tabs .hr-tabs-content,
  .cp-tabs .n-tabs-content {
    background-color: var(--cp-background-500) !important;
  }

  .cp-tabs .hr-tabs-tab,
  .cp-tabs .n-tabs-tab {
    background-color: transparent !important;
  }

  .cp-tabs .hr-tabs-tab:hover,
  .cp-tabs .n-tabs-tab:hover,
  .cp-tabs .hr-tabs-tab:focus,
  .cp-tabs .n-tabs-tab:focus,
  .cp-tabs .hr-tabs-tab:focus-visible,
  .cp-tabs .n-tabs-tab:focus-visible {
    background-color: var(--cp-hover) !important;
  }

  .cp-tabs .hr-tabs-tab--active,
  .cp-tabs .n-tabs-tab--active,
  .cp-tabs .hr-tabs-tab--active:hover,
  .cp-tabs .n-tabs-tab--active:hover {
    background-color: transparent !important;
  }

  /* Extra class beats the forced backgrounds above (and the inline --n-* bridge) on specificity. */
  .cp-tabs.cp-tabs--no-background {
    --n-color: transparent !important;
    --n-tab-color: transparent !important;
    background-color: transparent !important;
  }

  .cp-tabs.cp-tabs--no-background .hr-tabs-nav,
  .cp-tabs.cp-tabs--no-background .n-tabs-nav,
  .cp-tabs.cp-tabs--no-background .hr-tabs-nav-scroll-wrapper,
  .cp-tabs.cp-tabs--no-background .n-tabs-nav-scroll-wrapper,
  .cp-tabs.cp-tabs--no-background .hr-tabs-pane-wrapper,
  .cp-tabs.cp-tabs--no-background .n-tabs-pane-wrapper,
  .cp-tabs.cp-tabs--no-background .hr-tabs-content,
  .cp-tabs.cp-tabs--no-background .n-tabs-content {
    background-color: transparent !important;
  }

  .cp-tabs.hr-tabs--segment-type .hr-tabs-tab--active,
  .cp-tabs.n-tabs--segment-type .n-tabs-tab--active {
    background-color: color-mix(in oklab, var(--cp-primary-500) 10%, var(--cp-background-500)) !important;
  }
</style>
